import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Button, message, Steps, Spin, Alert, Collapse, Input, InputNumber, Select, Space, Divider } from 'antd';
import { EditOutlined, SaveOutlined, UndoOutlined } from '@ant-design/icons';
import { ContractDetails, Shipment, Container, Goods } from '../components/contract/types';
import { CardContainer } from '../components/contract/CardContainer';
import { SectionHeader } from '../components/contract/SectionHeader';
import { Title } from '../components/design-system/typography';
import { colors, spacing } from '../components/design-system/tokens';
import { 
  ContractGeneralInfoForm,
  ContractPartiesForm,
  ContractPaymentForm,
  ContractNotesForm 
} from '../components/contract/forms';
import { contractsApi } from '../services/api';
import { Company } from '../components/common/CompanyAutocomplete';

const { Step } = Steps;
const { Panel } = Collapse;
const { Option } = Select;
const { TextArea } = Input;

// Extended contract details with shipments from API
interface ExtendedContractData extends ContractDetails {
  shipments?: Shipment[];
}

const ContractUpdate: React.FC = () => {
  const navigate = useNavigate();
  const { reference } = useParams<{ reference: string }>();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [contractData, setContractData] = useState<ExtendedContractData | null>(null);
  const [originalData, setOriginalData] = useState<ExtendedContractData | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [savingItems, setSavingItems] = useState<Set<string>>(new Set());
  const [selectedSupplier, setSelectedSupplier] = useState<Company | null>(null);
  const [contractReference, setContractReference] = useState<string>('');
  const [contractReferenceOptions, setContractReferenceOptions] = useState<{value: string, year: string, label: string}[]>([]);

  const steps = [
    {
      title: 'General Information',
      content: 'contract-general'
    },
    {
      title: 'Parties',
      content: 'contract-parties'
    },
    {
      title: 'Payment Terms',
      content: 'contract-payment'
    },
    {
      title: 'Notes & Review',
      content: 'contract-notes'
    }
  ];

  // Track form changes
  const handleFormChange = useCallback(() => {
    setHasUnsavedChanges(true);
  }, []);

  const handleSupplierChange = async (supplier: Company) => {
    setSelectedSupplier(supplier);
    
    const supplierCode = supplier.shortCode || supplier.code || (supplier.name ? supplier.name.substring(0, 2).toUpperCase() : '');
    if (!supplierCode) {
      message.warning('Supplier has no code configured');
      return;
    }

    try {
      const response = await contractsApi.getNextReference(supplierCode);
      setContractReferenceOptions(response.options);
      const currentYearOption = response.options[1];
      if (currentYearOption) {
        setContractReference(currentYearOption.value);
        form.setFieldsValue({ rimtradeRef: currentYearOption.value });
      }
      setHasUnsavedChanges(true);
    } catch (error) {
      console.error('Failed to get next reference:', error);
      message.error('Failed to get contract reference options');
    }
  };

  const handleContractReferenceChange = (referenceValue: string) => {
    const selectedOption = contractReferenceOptions.find(opt => opt.value === referenceValue);
    if (selectedOption) {
      setContractReference(referenceValue);
      form.setFieldsValue({ rimtradeRef: referenceValue });
      setHasUnsavedChanges(true);
    }
  };

  useEffect(() => {
    if (reference) {
      loadContractData(reference);
    }
  }, [reference]);

  // Warn user about unsaved changes when leaving
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const loadContractData = async (contractReference: string) => {
    setIsLoading(true);
    try {
      const response = await contractsApi.getContract(contractReference) as any;
      
      const contract: ExtendedContractData = {
        id: response.id,
        reference: response.reference,
        generalInfo: {
          contractDate: response.generalInfo?.contractDate || '',
          buyerRef: response.generalInfo?.buyerRef || '',
          sellerRef: response.generalInfo?.sellerRef || '',
          revisionDate: response.generalInfo?.revisionDate || '',
          buyerSigned: response.generalInfo?.buyerSigned,
          sellerSigned: response.generalInfo?.sellerSigned,
        },
        parties: {
          supplier: {
            name: response.parties?.supplier?.name || '',
            address: response.parties?.supplier?.address || {
              street: '',
              city: '',
              state: '',
              postalCode: '',
              country: 'United States'
            }
          },
          seller: {
            name: response.parties?.seller?.name || 'Rimtrade, LLC',
            address: response.parties?.seller?.address || {
              street: '',
              city: '',
              state: '',
              postalCode: '',
              country: 'United States'
            }
          },
          buyer: {
            name: response.parties?.buyer?.name || '',
            address: response.parties?.buyer?.address || {
              street: '',
              city: '',
              state: '',
              postalCode: '',
              country: 'United States'
            }
          },
          brand: response.parties?.brand
        },
        paymentInfo: {
          sellerTerms: response.paymentInfo?.sellerTerms || '',
          sellerTermsId: response.paymentInfo?.sellerTermsId,
          buyerTerms: response.paymentInfo?.buyerTerms || '',
          buyerTermsId: response.paymentInfo?.buyerTermsId,
          sellerBasis: response.paymentInfo?.sellerBasis || '',
          sellerBasisId: response.paymentInfo?.sellerBasisId,
          buyerBasis: response.paymentInfo?.buyerBasis || '',
          buyerBasisId: response.paymentInfo?.buyerBasisId,
        },
        notes: {
          sellerNotes: response.notes?.sellerNotes || '',
          buyerNotes: response.notes?.buyerNotes || ''
        },
        shipments: response.shipments || []
      };

      setContractData(contract);
      setOriginalData(JSON.parse(JSON.stringify(contract))); // Deep clone for reset
      
      // Populate form
      form.setFieldsValue({
        contractDate: contract.generalInfo.contractDate,
        buyerRef: contract.generalInfo.buyerRef,
        sellerRef: contract.generalInfo.sellerRef,
        revisionDate: contract.generalInfo.revisionDate,
        buyerSigned: contract.generalInfo.buyerSigned,
        sellerSigned: contract.generalInfo.sellerSigned,
        
        supplier_name: contract.parties.supplier.name,
        supplier_street: contract.parties.supplier.address.street,
        supplier_city: contract.parties.supplier.address.city,
        supplier_state: contract.parties.supplier.address.state,
        supplier_postalCode: contract.parties.supplier.address.postalCode,
        supplier_country: contract.parties.supplier.address.country,
        
        seller_name: contract.parties.seller.name,
        seller_street: contract.parties.seller.address.street,
        seller_city: contract.parties.seller.address.city,
        seller_state: contract.parties.seller.address.state,
        seller_postalCode: contract.parties.seller.address.postalCode,
        seller_country: contract.parties.seller.address.country,
        
        buyer_name: contract.parties.buyer.name,
        buyer_street: contract.parties.buyer.address.street,
        buyer_city: contract.parties.buyer.address.city,
        buyer_state: contract.parties.buyer.address.state,
        buyer_postalCode: contract.parties.buyer.address.postalCode,
        buyer_country: contract.parties.buyer.address.country,
        
        brand: contract.parties.brand,
        
        sellerTerms: contract.paymentInfo.sellerTerms,
        sellerTermsId: contract.paymentInfo.sellerTermsId,
        buyerTerms: contract.paymentInfo.buyerTerms,
        buyerTermsId: contract.paymentInfo.buyerTermsId,
        sellerBasis: contract.paymentInfo.sellerBasis,
        sellerBasisId: contract.paymentInfo.sellerBasisId,
        buyerBasis: contract.paymentInfo.buyerBasis,
        buyerBasisId: contract.paymentInfo.buyerBasisId,
        
        sellerNotes: contract.notes.sellerNotes,
        buyerNotes: contract.notes.buyerNotes
      });
      
    } catch (error) {
      console.error('Failed to load contract:', error);
      message.error('Failed to load contract data');
      navigate('/contracts');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = async () => {
    try {
      const stepFields = getStepFields(currentStep);
      await form.validateFields(stepFields);
      setCurrentStep(currentStep + 1);
    } catch (error) {
      console.error('Validation failed:', error);
      message.error('Please fill in all required fields');
    }
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const getStepFields = (step: number): string[] => {
    switch (step) {
      case 0:
        return ['contractDate'];
      case 1:
        return ['supplier_name', 'buyer_name'];
      case 2:
        return [];
      case 3:
        return [];
      default:
        return [];
    }
  };

  // Reset form to original values
  const handleReset = () => {
    if (originalData) {
      setContractData(JSON.parse(JSON.stringify(originalData)));
      form.setFieldsValue({
        contractDate: originalData.generalInfo.contractDate,
        buyerRef: originalData.generalInfo.buyerRef,
        sellerRef: originalData.generalInfo.sellerRef,
        revisionDate: originalData.generalInfo.revisionDate,
        buyerSigned: originalData.generalInfo.buyerSigned,
        sellerSigned: originalData.generalInfo.sellerSigned,
        sellerNotes: originalData.notes.sellerNotes,
        buyerNotes: originalData.notes.buyerNotes
      });
      setHasUnsavedChanges(false);
      message.info('Changes reverted');
    }
  };

  // Handle cancel with confirmation if there are unsaved changes
  const handleCancel = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate(`/contracts/${reference}`);
      }
    } else {
      navigate(`/contracts/${reference}`);
    }
  };

  const handleSubmit = async (values: any) => {
    if (!contractData?.id) {
      message.error('Contract ID not found');
      return;
    }

    setIsSubmitting(true);
    
    // Optimistic update - show success immediately
    const hideLoading = message.loading('Saving changes...', 0);
    
    try {
      const parseDate = (dateStr: string | undefined): string | undefined => {
        if (!dateStr) return undefined;
        const date = new Date(dateStr);
        return isNaN(date.getTime()) ? undefined : date.toISOString().split('T')[0];
      };

      const updatePayload: any = {};

      // General info fields
      if (values.contractDate) {
        updatePayload.contractDate = parseDate(values.contractDate);
      }
      if (values.buyerRef !== undefined) {
        updatePayload.buyerRef = values.buyerRef;
      }
      if (values.sellerRef !== undefined) {
        updatePayload.sellerRef = values.sellerRef;
      }
      if (values.revisionDate) {
        updatePayload.revisionDate = parseDate(values.revisionDate);
      }
      if (values.buyerSigned) {
        updatePayload.buyerSigned = parseDate(values.buyerSigned);
      }
      if (values.sellerSigned) {
        updatePayload.sellerSigned = parseDate(values.sellerSigned);
      }

      // Payment terms and basis
      if (values.sellerTermsId) {
        updatePayload.termsSellerCd = values.sellerTermsId;
      }
      if (values.buyerTermsId) {
        updatePayload.termsBuyerCd = values.buyerTermsId;
      }
      if (values.sellerBasisId) {
        updatePayload.basisSellerCd = values.sellerBasisId;
      }
      if (values.buyerBasisId) {
        updatePayload.basisBuyerCd = values.buyerBasisId;
      }

      // Notes
      if (values.sellerNotes !== undefined) {
        updatePayload.sellerNotes = values.sellerNotes;
      }
      if (values.buyerNotes !== undefined) {
        updatePayload.buyerNotes = values.buyerNotes;
      }

      await contractsApi.updateContract(contractData.id, updatePayload);
      
      hideLoading();
      setHasUnsavedChanges(false);
      message.success('Contract updated successfully!');
      navigate(`/contracts/${reference}`);
    } catch (error: any) {
      hideLoading();
      console.error('Failed to update contract:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update contract. Please try again.';
      message.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Individual shipment/container/goods update handlers
  const handleUpdateShipment = async (shipmentId: string, updates: any) => {
    setSavingItems(prev => new Set(prev).add(`shipment-${shipmentId}`));
    try {
      await contractsApi.updateShipment(shipmentId, updates);
      
      // Optimistic update - update local state
      if (contractData?.shipments) {
        const updatedShipments = contractData.shipments.map(s => {
          if (s.id === shipmentId) {
            return { ...s, ...updates };
          }
          return s;
        });
        setContractData({ ...contractData, shipments: updatedShipments });
      }
      
      message.success('Shipment updated');
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to update shipment');
    } finally {
      setSavingItems(prev => {
        const next = new Set(prev);
        next.delete(`shipment-${shipmentId}`);
        return next;
      });
    }
  };

  const handleUpdateContainer = async (containerId: string, updates: any) => {
    setSavingItems(prev => new Set(prev).add(`container-${containerId}`));
    try {
      await contractsApi.updateContainer(containerId, updates);
      message.success('Container updated');
      // Reload to get fresh data
      if (reference) {
        await loadContractData(reference);
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to update container');
    } finally {
      setSavingItems(prev => {
        const next = new Set(prev);
        next.delete(`container-${containerId}`);
        return next;
      });
    }
  };

  const handleUpdateGood = async (goodId: string, updates: any) => {
    setSavingItems(prev => new Set(prev).add(`good-${goodId}`));
    try {
      await contractsApi.updateGood(goodId, updates);
      message.success('Goods updated');
      // Reload to get fresh data
      if (reference) {
        await loadContractData(reference);
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to update goods');
    } finally {
      setSavingItems(prev => {
        const next = new Set(prev);
        next.delete(`good-${goodId}`);
        return next;
      });
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <ContractGeneralInfoForm />;
      case 1:
        return (
          <ContractPartiesForm 
            onSupplierChange={handleSupplierChange}
            contractReferenceOptions={contractReferenceOptions}
            selectedReference={contractReference}
            onReferenceChange={handleContractReferenceChange}
          />
        );
      case 2:
        return <ContractPaymentForm />;
      case 3:
        return <ContractNotesForm />;
      default:
        return null;
    }
  };

  // Render shipments section for inline editing
  const renderShipmentsSection = () => {
    if (!contractData?.shipments?.length) {
      return (
        <Alert
          message="No Shipments"
          description="This contract has no shipments to edit."
          type="info"
          showIcon
        />
      );
    }

    return (
      <Collapse accordion>
        {contractData.shipments.map((shipment: any, index: number) => (
          <Panel 
            header={
              <Space>
                <strong>Shipment {shipment.shipmentNumber || index + 1}</strong>
                {shipment.reference && <span style={{ color: colors.text.secondary }}>({shipment.reference})</span>}
                {shipment.booking?.bookingNumber && (
                  <span style={{ color: colors.primary[60] }}>Booking: {shipment.booking.bookingNumber}</span>
                )}
              </Space>
            }
            key={shipment.id}
          >
            {/* Shipment Fields */}
            <div style={{ marginBottom: spacing.lg }}>
              <SectionHeader title="Shipment Details" />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: spacing.md, marginTop: spacing.md }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, color: colors.text.secondary }}>Timing</label>
                  <Input 
                    defaultValue={shipment.timing?.shipment}
                    placeholder="e.g., FH May / LH June"
                    onBlur={(e) => {
                      if (e.target.value !== shipment.timing?.shipment) {
                        handleUpdateShipment(shipment.id, { start: e.target.value.split('/')[0]?.trim(), end: e.target.value.split('/')[1]?.trim() });
                      }
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, color: colors.text.secondary }}>Ships With</label>
                  <Input 
                    defaultValue={shipment.timing?.shipsWith}
                    placeholder="Other shipment refs"
                    onBlur={(e) => {
                      if (e.target.value !== shipment.timing?.shipsWith) {
                        handleUpdateShipment(shipment.id, { ships_with: e.target.value });
                      }
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, color: colors.text.secondary }}>Packing Notes</label>
                  <Input 
                    defaultValue={shipment.timing?.packingNotes}
                    placeholder="Packing instructions"
                    onBlur={(e) => {
                      if (e.target.value !== shipment.timing?.packingNotes) {
                        handleUpdateShipment(shipment.id, { packing_notes: e.target.value });
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Containers */}
            {shipment.containers?.length > 0 && (
              <div style={{ marginTop: spacing.lg }}>
                <SectionHeader title="Containers" />
                {shipment.containers.map((container: any, cIndex: number) => (
                  <div 
                    key={container.id} 
                    style={{ 
                      padding: spacing.md, 
                      border: `1px solid ${colors.border.default}`,
                      borderRadius: 8,
                      marginTop: spacing.md,
                      backgroundColor: colors.background.primary
                    }}
                  >
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: spacing.md }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: 4, color: colors.text.secondary }}>Container #</label>
                        <Input 
                          defaultValue={container.details?.containerNumber || container.containerNumber}
                          placeholder="XXXX1234567"
                          onBlur={(e) => {
                            const currentVal = container.details?.containerNumber || container.containerNumber;
                            if (e.target.value !== currentVal) {
                              handleUpdateContainer(container.id, { container_number: e.target.value });
                            }
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: 4, color: colors.text.secondary }}>Seal #</label>
                        <Input 
                          defaultValue={container.details?.sealNumber}
                          placeholder="Seal number"
                          onBlur={(e) => {
                            if (e.target.value !== container.details?.sealNumber) {
                              handleUpdateContainer(container.id, { seal_number: e.target.value });
                            }
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: 4, color: colors.text.secondary }}>Equipment</label>
                        <Select 
                          defaultValue={container.details?.equipment}
                          style={{ width: '100%' }}
                          onChange={(value) => handleUpdateContainer(container.id, { equipment_type: value })}
                        >
                          <Option value="TWENTY_FT">20' Dry</Option>
                          <Option value="FORTY_FT">40' Dry</Option>
                          <Option value="FORTY_HC">40' HC</Option>
                          <Option value="TWENTY_REEFER">20' Reefer</Option>
                          <Option value="FORTY_REEFER">40' Reefer</Option>
                        </Select>
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: 4, color: colors.text.secondary }}>Net Weight (lbs)</label>
                        <InputNumber 
                          defaultValue={container.details?.netWeight}
                          style={{ width: '100%' }}
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val) && val !== container.details?.netWeight) {
                              handleUpdateContainer(container.id, { net_weight: val });
                            }
                          }}
                        />
                      </div>
                    </div>

                    {/* Goods in this container */}
                    {container.goods?.length > 0 && (
                      <div style={{ marginTop: spacing.md }}>
                        <Divider style={{ margin: `${spacing.sm} 0` }} />
                        <strong style={{ color: colors.text.secondary }}>Goods</strong>
                        {container.goods.map((good: any, gIndex: number) => (
                          <div 
                            key={good.id}
                            style={{ 
                              display: 'grid', 
                              gridTemplateColumns: 'repeat(5, 1fr)', 
                              gap: spacing.sm,
                              marginTop: spacing.sm,
                              padding: spacing.sm,
                              backgroundColor: colors.background.secondary,
                              borderRadius: 4
                            }}
                          >
                            <div>
                              <label style={{ fontSize: 11, color: colors.text.secondary }}>Commodity</label>
                              <div style={{ fontSize: 13 }}>{good.details?.commodity || 'N/A'}</div>
                            </div>
                            <div>
                              <label style={{ fontSize: 11, color: colors.text.secondary }}>Units</label>
                              <InputNumber 
                                size="small"
                                defaultValue={good.details?.units}
                                style={{ width: '100%' }}
                                onBlur={(e) => {
                                  const val = parseInt(e.target.value);
                                  if (!isNaN(val) && good.id) {
                                    handleUpdateGood(good.id as string, { num_units: val });
                                  }
                                }}
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: 11, color: colors.text.secondary }}>Lbs/Ea</label>
                              <InputNumber 
                                size="small"
                                defaultValue={parseFloat(good.details?.lbsEa) || undefined}
                                style={{ width: '100%' }}
                                onBlur={(e) => {
                                  const val = parseFloat(e.target.value);
                                  if (!isNaN(val) && good.id) {
                                    handleUpdateGood(good.id as string, { lbs_ea: val });
                                  }
                                }}
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: 11, color: colors.text.secondary }}>Price</label>
                              <Input 
                                size="small"
                                defaultValue={good.details?.price}
                                onBlur={(e) => {
                                  // Parse price like "$3.50/lb"
                                  const match = e.target.value.match(/\$?([\d.]+)/);
                                  if (match && match[1] && good.id) {
                                    handleUpdateGood(good.id as string, { price: parseFloat(match[1]) });
                                  }
                                }}
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: 11, color: colors.text.secondary }}>Buyer Price</label>
                              <Input 
                                size="small"
                                defaultValue={good.details?.buyerPrice}
                                onBlur={(e) => {
                                  const match = e.target.value.match(/\$?([\d.]+)/);
                                  if (match && match[1] && good.id) {
                                    handleUpdateGood(good.id as string, { buyer_price: parseFloat(match[1]) });
                                  }
                                }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Panel>
        ))}
      </Collapse>
    );
  };

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!contractData) {
    return (
      <div style={{ padding: spacing['2xl'] }}>
        <Alert
          message="Contract Not Found"
          description="The contract you're trying to edit could not be found."
          type="error"
          showIcon
          action={
            <Button onClick={() => navigate('/contracts')}>
              Back to Contracts
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{
      background: colors.background.primary,
      minHeight: 'calc(100vh - 112px)',
      padding: spacing['2xl']
    }}>
      <div style={{ 
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Page Header */}
        <div style={{ marginBottom: spacing['2xl'], display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Title>Edit Contract {contractData.reference}</Title>
            <p style={{ 
              color: colors.text.secondary,
              fontSize: '16px',
              marginTop: spacing.sm
            }}>
              Update the contract details below. Changes will be saved to the existing contract.
            </p>
          </div>
          {hasUnsavedChanges && (
            <Button 
              icon={<UndoOutlined />} 
              onClick={handleReset}
              style={{ marginTop: spacing.sm }}
            >
              Reset Changes
            </Button>
          )}
        </div>

        {/* Progress Steps */}
        <CardContainer variant="primary" style={{ marginBottom: spacing['2xl'] }}>
          <Steps current={currentStep} style={{ padding: `${spacing.lg} 0` }}>
            {steps.map(item => (
              <Step key={item.title} title={item.title} />
            ))}
          </Steps>
        </CardContainer>

        {/* Form Content */}
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={handleFormChange}
        >
          <CardContainer variant="secondary">
            <SectionHeader 
              title={steps[currentStep]?.title || 'Contract Update'}
              showBottomBorder={true}
            />
            
            <div style={{ padding: `${spacing['2xl']} 0` }}>
              {renderStepContent()}
            </div>

            {/* Navigation Buttons */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: spacing['2xl'],
              borderTop: `1px solid ${colors.border.default}`
            }}>
              <div>
                {currentStep > 0 && (
                  <Button onClick={handlePrev} size="large">
                    Previous
                  </Button>
                )}
              </div>

              <div style={{ display: 'flex', gap: spacing.md }}>
                <Button 
                  onClick={handleCancel}
                  size="large"
                >
                  Cancel
                </Button>
                
                {currentStep < steps.length - 1 ? (
                  <Button 
                    type="primary"
                    onClick={handleNext}
                    size="large"
                  >
                    Next
                  </Button>
                ) : (
                  <Button 
                    type="primary"
                    htmlType="submit"
                    loading={isSubmitting}
                    size="large"
                    icon={<SaveOutlined />}
                  >
                    Update Contract
                  </Button>
                )}
              </div>
            </div>
          </CardContainer>
        </Form>

        {/* Shipments Section - Always visible for inline editing */}
        {contractData.shipments && contractData.shipments.length > 0 && (
          <CardContainer variant="secondary" style={{ marginTop: spacing['2xl'] }}>
            <SectionHeader 
              title="Shipments, Containers & Goods"
              showBottomBorder={true}
            />
            <div style={{ padding: `${spacing.lg} 0` }}>
              <Alert 
                message="Inline Editing" 
                description="Changes to shipments, containers, and goods are saved automatically when you leave a field."
                type="info"
                showIcon
                style={{ marginBottom: spacing.lg }}
              />
              {renderShipmentsSection()}
            </div>
          </CardContainer>
        )}
      </div>
    </div>
  );
};

export default ContractUpdate;
